import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * The last line of defence: a render error anywhere below unmounts React's
 * whole tree, which the user experiences as a BLANK PAGE with the real
 * reason hidden in the console. This boundary catches it and shows the
 * error where the page was, so a crash is diagnosable on sight.
 */

interface Props { children: ReactNode }
interface State { error: Error | null; stack: string | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, stack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ stack: info.componentStack ?? null });
    console.error('[error-boundary]', error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <h1 className="text-lg font-semibold text-red-700">Something broke on this screen</h1>
        <p className="mt-2 text-sm text-slate-600">
          The error below is what a blank page hides. Share it when reporting the bug.
        </p>
        <pre className="mt-4 overflow-x-auto rounded-xl bg-slate-900 p-4 text-xs leading-relaxed text-red-300">
          {this.state.error.message}
          {this.state.stack ? `\n\nComponent stack:${this.state.stack}` : ''}
        </pre>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700"
        >
          Reload the page
        </button>
      </div>
    );
  }
}
